import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SystemApiService } from '@cv-invision/core';
import { Observable } from 'rxjs';
import { IntegrationsUrlParamsModel } from '../generic-integrations/models/integrations-url-params.model';

@Injectable()
export class GenericIntegrationService {

	private readonly BASE_URL = '/utils';

	get url(): string {
		return `${this.systemApiService.api.orders}${this.BASE_URL}`;
	}

	constructor(private httpClient: HttpClient, private systemApiService: SystemApiService) {
	}

	/**
	 * GET - Return the list of the integrations available.
	 */
	getIntegrations(): Observable<any> {
		return this.httpClient.get(`${this.url}/integrations`);
	}

	/**
	 * GET - Return the list of the integration Default Settings.
	 */
	getIntegrationDefaultSettings(integrationId: string): Observable<any> {
		return this.httpClient.get(`${this.url}/integrations/${integrationId}/default-settings`);
	}

	/**
	 * GET - Return the list of the integration Mappers.
	 */
	getIntegrationMappers(integrationId: string): Observable<any> {
		return this.httpClient.get(`${this.url}/integrations/${integrationId}/mappers`);
	}

	/**
	 * GET - Return the list of integrations
	 */
	getMappers(integrationId: string): Observable<any> {
		return this.getIntegrationMappers(integrationId);
	}

	/**
	 * GET - Return the list of the integration Mapper type.
	 */
	getIntegrationMapperType(integrationId: string, mapType: string): Observable<any> {
		return this.httpClient.get(`${this.url}/integrations/${integrationId}/mappers/${mapType}`);
	}

	/**
	 * GET - Return the list of the integration Documents.
	 */
	getIntegrationDocuments(integrationId: string): Observable<any> {
		return this.httpClient.get(`${this.url}/integrations/${integrationId}/document-types`);
	}

	/**
	 * GET - Return the list of the ClassValuation Documents.
	 */
	getClassDocuments(): Observable<any> {
		return this.httpClient.get(`${this.url}/classvaluation/document-types`);
	}

	/**
	 * GET - Return the list of the actions.
	 */
	getActionOptions(): Observable<any> {
		return this.httpClient.get(`${this.url}/enums/action`);
	}

	/**
	 * GET - Return the list of CMP on hold reasons.
	 */
	getCMPOnHoldReasons(): Observable<any> {
		return this.httpClient.get(`${this.url}/enums/cmp/on-hold-reasons`);
	}

	/**
	 * GET - Return the list of CMP on hold reasons.
	 */
	getReceiveFromClientOptions(): Observable<any> {
		return this.httpClient.get(`${this.url}/enums/receiveFromClient`);
	}

	/**
	 * GET - Return the list of the integration Products.
	 */
	getIntegrationProducts(integrationId: string): Observable<any> {
		return this.httpClient.get(`${this.url}/integrations/${integrationId}/product-types`);
	}

	/**
	 * GET - Return the list of the integration Organizations.
	 */
	getIntegrationOrganizations(integrationId: string): Observable<any> {
		return this.httpClient.get(`${this.url}/integrations/${integrationId}/organizations`);
	}

	/**
	 * Get the list of all available retry options for an integration
	 *
	 * @param integrationName
	 */
	getRetryActions(integrationName: string): Observable<any> {
		return this.httpClient.get(`${this.url}/integrations/${integrationName}/actions`);
	}

	/**
	 * Get generic integration section/mapper data
	 *
	 * @param integrationParams @see IntegrationsUrlParamsModel
	 * @returns
	 */
	getData(integrationParams: IntegrationsUrlParamsModel): Observable<any> {
		let url = this.getUrlForData(integrationParams);

		if(integrationParams.isExternalIntegration && integrationParams.customUrl){
			url = `${this.systemApiService.api.orders}/${integrationParams.customUrl}`;
		}

		return this.httpClient.get(url);
	}

	/**
	 * Save the data in the generic integration section/mapper
	 *
	 * @param integrationParams @see IntegrationsUrlParamsModel
	 * @param payload
	 * @returns
	 */
	saveData(integrationParams: IntegrationsUrlParamsModel, payload: any[]): Observable<any> {
		const url = this.getUrlForData(integrationParams, payload);
		return this.httpClient.put(url, payload);
	}

	/**
	 * Calculate the url for the generic integration endpoint, given the params
	 * @param integrationParams @see IntegrationsUrlParamsModel
	 * @returns
	 */
	private getUrlForData(integrationParams: IntegrationsUrlParamsModel, payload?: any): string {
		let url = `${this.url}/integrations/${integrationParams.integrationId}`;
		if (integrationParams.isMapper) {
			url = `${url}/mappers`;
		}

		url = `${url}/${integrationParams.section}`;

		if (integrationParams.section === 'organizations' && payload?.integrationId) {
			url = `${url}/${payload.integrationId}`;
		}

		if(integrationParams.isExternalIntegration && integrationParams.customSaveUrl) {
			url = `${this.systemApiService.api.orders}/${integrationParams.customSaveUrl}`;

			if(payload?.id){
				url = `${url}/${payload.id}`;
			}
		}

		return url;
	}

	/**
	 * Save the data in the organizations table
	 *
	 * @param integrationParams @see IntegrationsUrlParamsModel
	 * @param payload
	 * @returns
	 */
	savePostData(integrationParams: IntegrationsUrlParamsModel, payload: any[]): Observable<any> {
		const url = this.getUrlForData(integrationParams);
		return this.httpClient.post(url, payload);
	}

	/**
	 * Delete model
	 * @param integrationParams
	 * @param payload
	 * @returns
	 */
	deleteModel(integrationParams: IntegrationsUrlParamsModel, payload: any): Observable<any> {
		const url = this.getUrlForData(integrationParams, payload);
		return this.httpClient.delete(url);
	}
}
